import React, { useState } from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import {
  Alert, Button, Card, Input, Space, Spin, Tag, Typography, message,
} from 'antd';
import moment from 'moment';

import { useIntegrations } from '../../models/useIntegrations';

const { Text, Paragraph } = Typography;

const fmt = (ts) => (ts && typeof ts.toDate === 'function' ? moment(ts.toDate()).format('YYYY-MM-DD HH:mm') : null);

/**
 * The settings catalog: ops/integrations holds ONE flat field per setting;
 * this list is what the screen renders, so a new integration is one entry
 * here plus its consumer. A live doc value always wins over absence.
 */
export const KNOWN_INTEGRATIONS = [
  {
    field: 'paytipSlackWebhook',
    title: 'Pay+Tip requests → Slack',
    description: 'Incoming-webhook URL for #strike-team-pay-and-tip-request. '
      + 'Unset, requests still record on the team doc and the Pay+Tip Requests queue — only the Slack ping is skipped.',
    placeholder: 'https://hooks.slack.com/services/…',
  },
];

/** One setting's card: live value, edit-in-place, who set it last. */
const IntegrationCard = ({ spec, value, onSave }) => {
  const [draft, setDraft] = useState(null); // null = not editing
  const [saving, setSaving] = useState(false);

  const save = async (next) => {
    setSaving(true);
    try {
      await onSave(spec.field, next);
      message.success(next ? `${spec.title} saved` : `${spec.title} cleared`);
      setDraft(null);
    } catch (err) {
      console.error(`[integrations] save failed for ${spec.field}:`, err);
      message.error(err.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title={spec.title}
      extra={value ? <Tag color="green">Configured</Tag> : <Tag color="orange">Not set</Tag>}
      style={{ maxWidth: '760px', margin: '0 auto 16px', textAlign: 'left' }}
    >
      <Paragraph type="secondary">{spec.description}</Paragraph>
      {draft === null ? (
        <Space wrap>
          <Text code>{value || '—'}</Text>
          <Button size="small" onClick={() => setDraft(value || '')}>Edit</Button>
          {value && (
            <Button size="small" danger loading={saving} onClick={() => save(null)}>Clear</Button>
          )}
        </Space>
      ) : (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Input
            value={draft}
            placeholder={spec.placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onPressEnter={() => save(draft)}
          />
          <Button type="primary" loading={saving} onClick={() => save(draft)}>Save</Button>
          <Button onClick={() => setDraft(null)}>Cancel</Button>
        </div>
      )}
    </Card>
  );
};

/**
 * Ops → Integrations: ops/integrations rendered field by field from the
 * catalog. The doc's stamps say who last changed anything; per-field
 * history lives in the doc's history subcollection (console).
 */
const AdminIntegrationsBody = () => {
  const [loading, integrations, error, { setIntegrationField }] = useIntegrations();

  if (loading) return <Spin style={{ marginTop: '40px' }} />;
  if (error) return <Alert type="error" message={error} style={{ margin: '20px' }} />;

  const updated = fmt(integrations.updatedAt);

  return (
    <div style={{ padding: '0 16px' }}>
      {KNOWN_INTEGRATIONS.map((spec) => (
        <IntegrationCard
          key={spec.field}
          spec={spec}
          value={integrations[spec.field] || null}
          onSave={setIntegrationField}
        />
      ))}
      {updated && (
        <Text type="secondary">
          {`Last change ${updated} by ${integrations.updatedByName || integrations.updatedBy || 'unknown'}`}
        </Text>
      )}
    </div>
  );
};

export default AdminIntegrationsBody;
